import type { ImageAttachment } from '../../../types/imageAttachment';
import { postPresignedDownloadUrlsWithMeta } from '../../../api/imageDownload';
import {
  getUploadedImageFileRecords,
  postPresignedUploadUrls,
  postUploadMultiFileManage,
} from '../../../api/imageUpload';
import {
  buildImageUploadPlanBatch,
  type ImageUploadPlanItem,
  uploadImagePlanItems,
} from './imageUpload';

type UploadImageAttachmentsArgs = {
  wrId: number;
  images: ImageAttachment[];
  createBy?: string;
  updateBy?: string;
  onResetImages?: () => void;
  signal?: AbortSignal;
};

type FileAttachment = Extract<ImageAttachment, { kind: 'file' }>;

type DownloadPreview = {
  url: string;
  expiresAtMs?: number;
};

const isFileAttachment = (img: ImageAttachment): img is FileAttachment =>
  img.kind === 'file';

const getFileKey = (img: ImageAttachment): string => img.fileKey ?? '';

const readFileIdsByKey = async (
  wrId: number,
  signal?: AbortSignal
): Promise<Map<string, number>> => {
  const records = await getUploadedImageFileRecords(wrId, signal);
  const ids = new Map<string, number>();

  for (const record of records) {
    const previous = ids.get(record.object_key);

    if (previous !== undefined && previous !== record.file_id) {
      throw new Error('Multiple saved images have the same object key');
    }

    ids.set(record.object_key, record.file_id);
  }

  return ids;
};

/** Recover IDs left unresolved after a successful save and a failed refresh. */
const resolveSavedImageIds = async (
  wrId: number,
  images: ImageAttachment[],
  signal?: AbortSignal
): Promise<ImageAttachment[]> => {
  const needsLookup = images.some(
    (img) => img.file_id == null && Boolean(getFileKey(img))
  );

  if (!needsLookup) return images;

  const ids = await readFileIdsByKey(wrId, signal);

  return images.map((img) => {
    if (img.file_id != null || !getFileKey(img)) return img;

    const fileId = ids.get(getFileKey(img));

    if (fileId === undefined) {
      // Do not submit a replacement list that could delete the saved image.
      throw new Error('Saved image ID could not be resolved; refresh before retrying');
    }

    return { ...img, file_id: fileId };
  });
};

export const uploadImageAttachments = async ({
  wrId,
  images,
  createBy,
  updateBy,
  onResetImages,
  signal,
}: UploadImageAttachmentsArgs): Promise<ImageAttachment[]> => {
  if (!Number.isSafeInteger(wrId) || wrId <= 0) {
    throw new Error('A valid wr_id is required');
  }

  if (signal?.aborted) {
    throw new DOMException('Image upload was aborted', 'AbortError');
  }

  const preparedImages = await resolveSavedImageIds(wrId, images, signal);
  const imageIds = new Set<string>();

  for (const img of preparedImages) {
    if (imageIds.has(img.id)) {
      throw new Error('Duplicate image attachment ID');
    }

    imageIds.add(img.id);

    if (img.file_id != null) {
      if (!Number.isSafeInteger(img.file_id) || img.file_id <= 0) {
        throw new Error('Invalid saved image ID');
      }
    } else if (img.kind === 'url' && !/^https?:\/\//i.test(img.name)) {
      // A signed preview URL is not an identity and must not be persisted as one.
      throw new Error('Saved image metadata is missing; refresh before retrying');
    }
  }

  const newImages = preparedImages
    .filter(isFileAttachment)
    .filter((img) => img.file_id == null);

  const uploadFiles = newImages.map((img) => img.file);
  const uploadPlanById = new Map<string, ImageUploadPlanItem>();

  if (uploadFiles.length > 0) {
    const plan = await buildImageUploadPlanBatch(
      uploadFiles,
      (payloads, s) => postPresignedUploadUrls(payloads, wrId, s ?? signal),
      undefined,
      signal
    );

    const keys = new Set<string>();

    for (const [index, item] of plan.items.entries()) {
      const key = item.presign.fileKey;

      if (!key || !item.presign.bucket || keys.has(key)) {
        throw new Error('Upload plan is missing a unique key or bucket');
      }

      keys.add(key);
      uploadPlanById.set(newImages[index].id, item);
    }

    await uploadImagePlanItems(plan.items, signal);
  }

  const filesPayload = preparedImages.map((img, index) => {
    const order_number = index + 1;

    if (img.file_id != null) {
      return {
        file_id: img.file_id,
        fileName: img.name || 'image',
        order_number,
      };
    }

    if (img.kind === 'file') {
      const item = uploadPlanById.get(img.id);

      if (!item) {
        throw new Error('Missing upload plan for new image');
      }

      return {
        file_id: null,
        bucket: item.presign.bucket,
        key: item.presign.fileKey,
        fileName: item.presign.fileName ?? (img.file.name || 'image'),
        contentType: item.presign.contentType ??
          (img.file.type || 'application/octet-stream'),
        size_bytes: img.file.size,
        order_number,
      };
    }

    // Preserve the existing external-URL API contract; the URL tab stays disabled.
    return {
      file_id: null,
      fileName: img.name,
      order_number,
    };
  });

  await postUploadMultiFileManage(
    {
      wr_id: wrId,
      files: filesPayload,
      create_by: createBy,
      update_by: updateBy,
    },
    signal
  );

  // The save succeeded. Retain keys even if subsequent GETs fail.
  // Otherwise the next add would resend saved images as new images.
  const savedImages: ImageAttachment[] = preparedImages.map((img) => {
    if (img.file_id != null) return img;

    const item = img.kind === 'file'
      ? uploadPlanById.get(img.id)
      : undefined;

    return {
      ...img,
      fileKey: item?.presign.fileKey ??
        (img.kind === 'url' ? img.name : getFileKey(img)),
      name: item?.presign.fileName ?? img.name,
    };
  });

  let idsByKey = new Map<string, number>();

  if (savedImages.some((img) => img.file_id == null)) {
    try {
      idsByKey = await readFileIdsByKey(wrId, signal);
    } catch {
      // Do not report a completed save as failed or trigger the caller's rollback.
      console.warn('Images saved, but file IDs could not be refreshed');
    }
  }

  const downloads = new Map<string, DownloadPreview>();

  const downloadKeys = Array.from(
    new Set(
      savedImages
        .filter(isFileAttachment)
        .map(getFileKey)
        .filter(Boolean)
    )
  );

  if (downloadKeys.length > 0) {
    try {
      const result = await postPresignedDownloadUrlsWithMeta(
        downloadKeys,
        signal
      );

      for (const item of result.items) {
        if (item?.key && item?.url) {
          downloads.set(item.key, {
            url: item.url,
            expiresAtMs: result.expiresAtMs,
          });
        }
      }
    } catch {
      console.warn('Images saved, but preview URLs could not be refreshed');
    }
  }

  const updatedImages = savedImages.map((img): ImageAttachment => {
    const key = getFileKey(img);
    const fileId = img.file_id ?? idsByKey.get(key) ?? null;

    if (img.kind !== 'file') {
      return {
        ...img,
        file_id: fileId,
      };
    }

    const download = downloads.get(key);

    if (!download) {
      // The caller releases the previous blob URL when replacing its state.
      // Keep a fresh local preview if the remote preview is unavailable.
      let previewUrl = img.previewUrl;

      if (!onResetImages && previewUrl.startsWith('blob:')) {
        try {
          previewUrl = URL.createObjectURL(img.file);
        } catch {
          console.warn('Images saved, but local preview could not be refreshed');
        }
      }

      return {
        ...img,
        previewUrl,
        file_id: fileId,
      };
    }

    if (img.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(img.previewUrl);
    }

    return {
      id: img.id,
      kind: 'url',
      url: download.url,
      previewUrl: download.url,
      name: img.name,
      file_id: fileId,
      fileKey: key,
      previewUrlExpiresAtMs: download.expiresAtMs,
    };
  });

  onResetImages?.();

  return updatedImages;
};